<script setup lang="ts">
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import Button from "@/design/primitives/Button.vue";
import {
	type ApprovalState,
	approverOf,
	type ChatApproval,
	isDiscarded,
} from "../composables/approvals";
import { useAgentsI18n } from "../i18n/use-agents-i18n";

/**
 * O botão "Aprovar" logo abaixo da fala do agente.
 *
 * Mostra o TEXTO EXATO que passa a valer: aprovar é sobre o que se vê. O estado
 * vem do servidor (`state`), então o cartão reaberto amanhã diz "Aprovado" (ou
 * "descartada") em vez de oferecer o clique de novo.
 */
const props = defineProps<{ approval: ChatApproval }>();

const { t, te } = useAgentsI18n();
const queries = useQueryClient();
const approver = computed(() => approverOf(props.approval.kind));

const queryKey = ["approval", props.approval.kind, props.approval.id];
const status = useQuery({
	queryKey,
	queryFn: (): Promise<ApprovalState> =>
		approver.value?.state(props.approval) ?? Promise.resolve("pending"),
	staleTime: Number.POSITIVE_INFINITY,
});

const busy = ref(false);
const error = ref("");
const settled = (state: ApprovalState) => queries.setQueryData(queryKey, state);

const title = computed(() => {
	const key = `approval.title.${props.approval.kind}`;
	return te(key) ? t(key) : t("approval.title.generic");
});
const hint = computed(() => {
	const p = props.approval.payload ?? {};
	if (typeof p.agent === "string") return t("approval.forAgent", { agent: p.agent });
	if (p.replaces === true) return t("approval.replaces");
	return p.destination === "library" ? t("approval.toLibrary") : "";
});

async function approve(): Promise<void> {
	if (!approver.value) return;
	busy.value = true;
	error.value = "";
	try {
		await approver.value.approve(props.approval);
		settled("approved");
		for (const key of approver.value.refresh)
			void queries.invalidateQueries({ queryKey: [...key] });
	} catch (e) {
		const code = (e as { payload?: { error?: string } }).payload?.error;
		// Aprovado noutra aba ou na Memória: o resultado é o mesmo que o pedido.
		if (code === "ALREADY_PROMOTED") {
			settled("approved");
		} else if (isDiscarded(e)) {
			settled("discarded");
		} else {
			error.value = t("approval.failed");
		}
	} finally {
		busy.value = false;
	}
}
</script>

<template>
	<div class="space-y-2 rounded-md border border-border bg-bg-subtle px-3 py-2.5 text-xs">
		<div class="flex items-center justify-between gap-2">
			<span class="font-medium text-fg">{{ title }}</span>
			<span v-if="hint" class="text-2xs text-fg-subtle">{{ hint }}</span>
		</div>
		<p class="whitespace-pre-line text-fg">{{ approval.text }}</p>
		<p v-if="error" class="text-danger">{{ error }}</p>
		<p v-if="status.data.value === 'approved'" class="font-medium text-success">
			{{ t("approval.done") }}
		</p>
		<p v-else-if="status.data.value === 'discarded'" class="text-fg-subtle">
			{{ t("approval.discarded") }}
		</p>
		<Button
			v-else
			size="sm"
			:loading="busy"
			:disabled="status.isLoading.value"
			@click="approve"
		>
			{{ t("approval.approve") }}
		</Button>
	</div>
</template>
